/**
 * QR1 · 凭证 QR 客户评分/投诉页（公开·无 auth·移动端·扫码开）
 * 同页两功能区：①投诉区(行程中·实时推 OP 拦差评·可多次) ②评分区(行程末·打星·一次)。
 * 自包含 useState（不依赖 admin auth/Layout/QueryClient）。
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getReviewContext, submitComplaint, submitRating, type ReviewContext } from "@/services/publicReview";
import { useBrandShort } from "@/shared/branding";

export function CustomerReviewPage() {
  const { token } = useParams<{ token: string }>();
  const brand = useBrandShort();   // BRANDUI-1 · 读系统设置的品牌名；没填 = 不显示名字
  const [ctx, setCtx] = useState<ReviewContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    getReviewContext(token)
      .then(setCtx)
      .catch((e) => setError(e?.message?.includes("404") || e?.status === 404 ? "链接无效或已过期" : "加载失败，请稍后重试"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (loading) {
    return <Shell brand={brand}><div className="rounded-xl bg-white p-6 text-center text-slate-400">加载中…</div></Shell>;
  }
  if (error || !ctx) {
    return (
      <Shell brand={brand}>
        <div className="rounded-xl border border-rose-100 bg-white p-6 text-center">
          <p className="text-3xl">🔗</p>
          <p className="mt-2 text-sm font-medium text-slate-700">{error ?? "链接无效"}</p>
          <p className="mt-1 text-xs text-slate-400">请通过最新的电子凭证重新扫码</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell brand={brand}>
      {/* 行程信息卡（零敏感·无金额） */}
      <div className="rounded-2xl bg-white p-5 shadow-sm">
        <p className="text-xs text-slate-400">您好，{ctx.customer_name}</p>
        <p className="mt-1 text-base font-semibold text-slate-900">{ctx.product_name ?? "您的行程"}</p>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-slate-600">
          {ctx.driver_name && <span>🧑‍✈️ 司导 {ctx.driver_name}</span>}
          {ctx.travel_date && <span>📅 {ctx.travel_date}</span>}
        </div>
      </div>

      <ComplaintSection token={token!} categories={ctx.categories} />
      <RatingSection token={token!} alreadyRated={ctx.review_submitted} driverName={ctx.driver_name} />

      <p className="px-2 pb-2 text-center text-[11px] text-slate-400">{brand ? `${brand} · ` : ""}感谢您的反馈</p>
    </Shell>
  );
}

function Shell({ children, brand }: { children: React.ReactNode; brand: string }) {
  return (
    <main className="min-h-screen bg-slate-50 pb-10">
      <header className="bg-slate-900 px-5 pb-7 pt-6 text-white">
        <div className="flex items-center gap-2">
          <span className="text-lg">🌲</span>
          {brand && <span className="text-lg font-bold">{brand}</span>}
        </div>
        <p className="mt-1 text-xs text-slate-300">行程反馈 · 您的声音帮助我们做得更好</p>
      </header>
      <section className="flex flex-col gap-4 px-4 pt-5">{children}</section>
    </main>
  );
}

/** ① 投诉区（行程中·实时推 OP·可多次提） */
function ComplaintSection({ token, categories }: { token: string; categories: { value: string; label: string }[] }) {
  const [category, setCategory] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!category) { setErr("请选择问题类型"); return; }
    setBusy(true); setErr(null);
    try {
      await submitComplaint(token, category, content.trim());
      setDone(true);
      setContent("");
      setCategory("");
    } catch (e: any) {
      setErr(e?.message?.includes("429") ? "提交过于频繁，请稍后再试" : "提交失败，请重试");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-5">
      <div className="flex items-center gap-2">
        <span className="text-lg">🚨</span>
        <h2 className="text-[15px] font-bold text-amber-900">遇到问题？立即反馈</h2>
      </div>
      <p className="mt-1 text-[12px] text-amber-700">行程中遇到任何问题，立即提交，客服会马上联系处理。</p>

      {done && (
        <div className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-[13px] font-medium text-emerald-700">
          ✓ 已通知客服，马上为您处理。如还有其它问题可继续提交。
          <button onClick={() => setDone(false)} className="ml-2 text-emerald-600 underline">再提交一条</button>
        </div>
      )}

      {!done && (
        <div className="mt-3 flex flex-col gap-2.5">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full rounded-lg border border-amber-300 bg-white px-3 py-3 text-[15px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            <option value="">请选择问题类型…</option>
            {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="描述一下遇到的问题（选填）…"
            className="w-full rounded-lg border border-amber-300 bg-white px-3 py-2.5 text-[14px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
          {err && <p className="text-[12px] text-rose-600">{err}</p>}
          <button
            onClick={submit}
            disabled={busy}
            className="w-full rounded-xl bg-amber-600 py-3.5 text-[15px] font-semibold text-white active:bg-amber-700 disabled:opacity-50"
          >
            {busy ? "提交中…" : "提交反馈"}
          </button>
        </div>
      )}
    </div>
  );
}

/** ② 评分区（行程末·打星·一次） */
function RatingSection({ token, alreadyRated, driverName }: { token: string; alreadyRated: boolean; driverName: string | null }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(alreadyRated);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (rating < 1) { setErr("请先选择星级"); return; }
    setBusy(true); setErr(null);
    try {
      await submitRating(token, rating, comment.trim());
      setDone(true);
    } catch (e: any) {
      if (e?.message?.includes("409")) { setDone(true); }
      else setErr(e?.message?.includes("429") ? "提交过于频繁，请稍后再试" : "提交失败，请重试");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-blue-100 bg-white p-5 text-center">
        <p className="text-3xl">🙏</p>
        <p className="mt-2 text-[15px] font-semibold text-slate-800">感谢您的评价！</p>
        <p className="mt-1 text-[12px] text-slate-400">您的反馈已收到</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-blue-100 bg-white p-5">
      <div className="flex items-center gap-2">
        <span className="text-lg">⭐</span>
        <h2 className="text-[15px] font-bold text-slate-900">行程结束，给{driverName ? `司导 ${driverName}` : "司导"}打个分</h2>
      </div>
      <div className="mt-4 flex justify-center gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setRating(n)} className="text-4xl active:scale-95" aria-label={`${n} 星`}>
            <span className={n <= rating ? "" : "grayscale opacity-30"}>⭐</span>
          </button>
        ))}
      </div>
      {rating > 0 && <p className="mt-1 text-center text-[12px] text-slate-500">{["", "很不满意", "不满意", "一般", "满意", "非常满意"][rating]}</p>}
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={3}
        maxLength={2000}
        placeholder="说说您的体验（选填）…"
        className="mt-3 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-[14px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
      />
      {err && <p className="mt-1 text-[12px] text-rose-600">{err}</p>}
      <button
        onClick={submit}
        disabled={busy}
        className="mt-3 w-full rounded-xl bg-blue-600 py-3.5 text-[15px] font-semibold text-white active:bg-blue-700 disabled:opacity-50"
      >
        {busy ? "提交中…" : "提交评价"}
      </button>
    </div>
  );
}
